/**
 * User authentication menu component.
 * Anonymous users can use the app freely.
 * Logged-in users see their account info and can sign out.
 * Supports GitHub/Google OAuth and email/password registration.
 */

import { useState } from "react";
import {
  Button,
  Menu,
  Text,
  Loader,
  Modal,
  TextInput,
  PasswordInput,
  Stack,
  Anchor,
  Alert,
  Divider,
} from "@mantine/core";
import {
  IconUser,
  IconLogin,
  IconUserPlus,
  IconBrandGithub,
  IconBrandGoogle,
} from "@tabler/icons-react";
import { useUser, useUserLoading, useViewerStore } from "../store";
import { apiClient } from "../api";
import { useAuthForm, type AuthView } from "./useAuthForm";

export function UserMenu() {
  const user = useUser();
  const loading = useUserLoading();
  const setUser = useViewerStore((state) => state.setUser);

  const [modalOpen, setModalOpen] = useState(false);
  const {
    authView,
    setAuthView,
    submitting,
    error,
    setError,
    authConfig,
    loginId,
    setLoginId,
    loginPassword,
    setLoginPassword,
    regUsername,
    setRegUsername,
    regEmail,
    setRegEmail,
    regPassword,
    setRegPassword,
    regConfirmPassword,
    setRegConfirmPassword,
    regDisplayName,
    setRegDisplayName,
    resetForms,
    handleLogin,
    handleRegister,
  } = useAuthForm({
    // Signing in used to clear the fields here as well; openModal already does
    // that on the way back in, which is the only way they become visible again.
    onAuthenticated: () => setModalOpen(false),
    onAuthError: () => setModalOpen(true),
  });

  const openModal = (view: AuthView) => {
    resetForms();
    setAuthView(view);
    setModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      await apiClient.logout();
    } catch {
      // Clear local state even if request fails
    }
    setUser(null);
  };

  const hasOAuth = authConfig?.github_enabled || authConfig?.google_enabled;

  if (loading) {
    return <Loader size="xs" />;
  }

  // Authenticated user — show account menu
  if (user) {
    return (
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Button size="xs" variant="subtle" leftSection={<IconUser size={14} />}>
            {user.display_name || user.username}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Account</Menu.Label>
          <Menu.Item disabled>
            <Text size="xs" c="dimmed">@{user.username}</Text>
          </Menu.Item>
          {user.email && (
            <Menu.Item disabled>
              <Text size="xs" c="dimmed">{user.email}</Text>
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item color="red" onClick={handleLogout}>
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }

  // Anonymous user — show sign-in button + modal
  return (
    <>
      <Button
        size="compact-sm"
        variant="filled"
        leftSection={<IconLogin size={14} />}
        onClick={() => openModal("login")}
      >
        Sign in
      </Button>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={authView === "login" ? "Sign in" : "Create account"}
        centered
        size="sm"
      >
        {error && (
          <Alert color="red" mb="md" variant="light">
            {error}
          </Alert>
        )}

        {/* OAuth buttons */}
        {hasOAuth && (
          <>
            <Stack gap="xs">
              {authConfig?.github_enabled && (
                <Button
                  fullWidth
                  variant="default"
                  leftSection={<IconBrandGithub size={18} />}
                  onClick={() => apiClient.loginWithGithub()}
                >
                  {authView === "login" ? "Continue with GitHub" : "Sign up with GitHub"}
                </Button>
              )}
              {authConfig?.google_enabled && (
                <Button
                  fullWidth
                  variant="default"
                  leftSection={<IconBrandGoogle size={18} />}
                  onClick={() => apiClient.loginWithGoogle()}
                >
                  {authView === "login" ? "Continue with Google" : "Sign up with Google"}
                </Button>
              )}
            </Stack>
            <Divider
              my="md"
              label={authView === "login" ? "or sign in with email" : "or register with email"}
              labelPosition="center"
            />
          </>
        )}

        {authView === "login" ? (
          <Stack gap="sm">
            <TextInput
              label="Username or email"
              placeholder="Enter your username or email"
              value={loginId}
              onChange={(e) => setLoginId(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            <Button fullWidth onClick={handleLogin} loading={submitting} mt="xs">
              Sign in
            </Button>
            <Text size="sm" ta="center" c="dimmed">
              Don&apos;t have an account?{" "}
              <Anchor
                component="button"
                size="sm"
                onClick={() => { setError(null); setAuthView("register"); }}
              >
                Register
              </Anchor>
            </Text>
          </Stack>
        ) : (
          <Stack gap="sm">
            <TextInput
              label="Username"
              placeholder="Choose a username"
              required
              value={regUsername}
              onChange={(e) => setRegUsername(e.currentTarget.value)}
            />
            <TextInput
              label="Email"
              placeholder="your@email.com"
              required
              value={regEmail}
              onChange={(e) => setRegEmail(e.currentTarget.value)}
            />
            <TextInput
              label="Display name"
              placeholder="How you want to appear (optional)"
              value={regDisplayName}
              onChange={(e) => setRegDisplayName(e.currentTarget.value)}
            />
            <PasswordInput
              label="Password"
              placeholder="At least 8 characters"
              required
              value={regPassword}
              onChange={(e) => setRegPassword(e.currentTarget.value)}
            />
            <PasswordInput
              label="Confirm password"
              placeholder="Re-enter your password"
              required
              value={regConfirmPassword}
              onChange={(e) => setRegConfirmPassword(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
            />
            <Button
              fullWidth
              leftSection={<IconUserPlus size={16} />}
              onClick={handleRegister}
              loading={submitting}
              mt="xs"
            >
              Create account
            </Button>
            <Text size="sm" ta="center" c="dimmed">
              Already have an account?{" "}
              <Anchor
                component="button"
                size="sm"
                onClick={() => { setError(null); setAuthView("login"); }}
              >
                Sign in
              </Anchor>
            </Text>
          </Stack>
        )}
      </Modal>
    </>
  );
}
